import { ApexBarChart } from '@/components'
import { generatePeriod } from '@/pages/main/_components/golfSearch/useGolfSearchData'
import {
  GraphDataState,
  MonthAvgPriceByRegionItem,
} from '@/redux/slices/golfSearchSlice'
import styled from 'styled-components'

import { useResponsive } from '@/hooks/useResponsive'
interface MonthGraphProps {
  monthData: GraphDataState['monthData']
  monthAvgPriceByRegion?: GraphDataState['monthAvgPriceByRegion']
  isDualGraph?: boolean
  areaCd?: string | null
  areaNm?: string
}

export const MonthGraph = ({
  monthData,
  monthAvgPriceByRegion,
  isDualGraph,
  areaCd,
  areaNm,
}: MonthGraphProps) => {
  const safeGraphData = monthData?.length ? monthData : []
  const safeRegionGraphData = monthAvgPriceByRegion?.length
    ? monthAvgPriceByRegion
    : []

  const isNonMemberDataEmpty = safeGraphData[0]?.every((item) => item === 0)
  const isMemberDataEmpty = safeGraphData[2]?.every((item) => item === 0)

  const { isMobile, isTablet, isDesktop } = useResponsive()

  const monthsArr = generatePeriod('month')

  const mapRegionAvgToGraph = (
    regionData: MonthAvgPriceByRegionItem[] = [],
  ): number[][] => {
    return regionData.reduce(
      (acc, item) => {
        const monthIndex = monthsArr.findIndex(
          (m) => m === `${item.year.slice(-2)}-${item.month.padStart(2, '0')}`,
        )
        if (monthIndex >= 0) {
          acc[0][monthIndex] = item.n01
          acc[1][monthIndex] = item.n02
          acc[2][monthIndex] = item.m01
          acc[3][monthIndex] = item.m02
        }
        return acc
      },
      [
        new Array(12).fill(0),
        new Array(12).fill(0),
        new Array(12).fill(0),
        new Array(12).fill(0),
      ],
    )
  }

  const regionGraphData = mapRegionAvgToGraph(safeRegionGraphData)

  /** 가격 비교 텍스트 생성 */
  const getPriceDifferenceText = (
    currentPrice: number,
    regionPrice: number,
  ): React.ReactElement => {
    const difference = currentPrice - regionPrice
    const areaText = areaNm
      ? `${areaNm} `
      : !areaCd || areaCd === '00'
        ? '전국 '
        : '같은 지역 '

    return difference === 0 ? (
      <>{areaText} 평균과 같은 가격입니다.</>
    ) : (
      <>
        {areaText} 평균보다{' '}
        <span style={{ color: difference > 0 ? '#ff4d4f' : '#1890ff' }}>
          {Math.abs(difference).toLocaleString('ko-KR')}원
        </span>{' '}
        {difference > 0 ? '높아요' : '낮아요'}
      </>
    )
  }

  const commonOptions: ApexCharts.ApexOptions = {
    chart: { type: 'line', height: 350, toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: '55%' } },
    dataLabels: { enabled: false },
    stroke: { width: 2, curve: 'smooth' },
    markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
    xaxis: {
      categories: monthsArr,
      labels: {
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: {
      title: { text: '단위 (원)' },
      labels: { formatter: (v: any) => v.toLocaleString('ko-KR') },
    },
    fill: { opacity: 1 },
  }

  const colorPalette = {
    member: ['#FFB6C1', '#FF69B4'], // 회원 색상
    region: ['#9370DB', '#8FBC8F'], // 지역 평균 색상
  }

  const nonMemberData = [
    {
      name: '비회원(주중)',
      data: safeGraphData[0] || [0],
    },
    {
      name: '비회원(주말)',
      data: safeGraphData[1] || [0],
    },
    {
      name: `${safeRegionGraphData[0]?.areaNm}(주중)`,
      data: regionGraphData[0] || [0],
      color: colorPalette.region[0],
      type: 'line',
      dashArray: 5,
    },
    {
      name: `${safeRegionGraphData[0]?.areaNm}(주말)`,
      data: regionGraphData[1] || [0],
      color: colorPalette.region[1],
      type: 'line',
      dashArray: 5,
    },
  ]

  const memberData = [
    {
      name: '회원(주중)',
      data: safeGraphData[2] || [0],
      color: colorPalette.member[0],
    },
    {
      name: '회원(주말)',
      data: safeGraphData[3] || [0],
      color: colorPalette.member[1],
    },
    {
      name: `${safeRegionGraphData[0]?.areaNm}(주중)`,
      data: regionGraphData[2] || [0],
      color: colorPalette.region[0],
      type: 'line',
      dashArray: 5,
    },
    {
      name: `${safeRegionGraphData[0]?.areaNm}(주말)`,
      data: regionGraphData[3] || [0],
      color: colorPalette.region[1],
      type: 'line',
      dashArray: 5,
    },
  ]

  return (
    <GraphWrapper>
      <GraphTitle>{areaNm ? `${areaNm} 지역 평균` : '월 평균'}</GraphTitle>

      {!isNonMemberDataEmpty && (
        <>
          <GraphText>
            <span style={{ fontSize: '12px' }}>
              (주중) :{' '}
              {getPriceDifferenceText(
                safeGraphData[0][11],
                regionGraphData[0][11],
              )}
            </span>
            <br />
            <span style={{ fontSize: '12px' }}>
              (주말) :{' '}
              {getPriceDifferenceText(
                safeGraphData[1][11],
                regionGraphData[1][11],
              )}
            </span>
          </GraphText>
          <ApexBarChart
            options={{
              ...commonOptions,
              title: {
                text: '비회원',
                align: 'left',
                style: { fontSize: '16px', fontWeight: 'bold' },
              },
            }}
            height={isMobile ? '280px' : '350px'}
            width="100%"
            series={nonMemberData}
          />
        </>
      )}

      {!isMemberDataEmpty && (isDualGraph || isNonMemberDataEmpty) && (
        <>
          <GraphText>
            <span style={{ fontSize: '12px' }}>
              (주중) :{' '}
              {getPriceDifferenceText(
                safeGraphData[2][11],
                regionGraphData[2][11],
              )}
            </span>
            <br />
            <span style={{ fontSize: '12px' }}>
              (주말) :{' '}
              {getPriceDifferenceText(
                safeGraphData[3][11],
                regionGraphData[3][11],
              )}
            </span>
          </GraphText>
          <ApexBarChart
            options={{
              ...commonOptions,
              title: {
                text: '회원',
                align: 'left',
                style: { fontSize: '16px', fontWeight: 'bold' },
              },
            }}
            width="100%"
            series={memberData}
          />
        </>
      )}
    </GraphWrapper>
  )
}

const GraphWrapper = styled.div`
  width: 100%;
  position: relative;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 10px;
    max-width: 100%;
    overflow-x: auto;
  }
`

const GraphTitle = styled.p`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 10px;
`

const GraphText = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
`
